/* ═══════════════════════════════════════════════════════════════════════
   FizzyIQ — "Find your fit" product quiz engine.
   One question per screen; state persists on back-nav. Audience-aware copy,
   conditional slides (showIf), a multi-select ranked flavor step, and a
   PDP-style outcome buy box (product image + age + flavor + plan) that adds
   straight to the cart. Opens from [data-fizzy-quiz-open] or a link to "#quiz".
   Klaviyo wiring is deferred — the email is console.logged for now.
   Sam: 2026-06-07.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  if (window.__fizzyQuizBooted) return;
  window.__fizzyQuizBooted = true;

  var PRODUCT_URL = '/products/fizzyfocus';
  var DISCOUNT_CODE = 'QUIZ10';   // extra 10% off — auto-applied at checkout on submit

  // Prices pull from live variant data injected by fizzy-quiz.liquid
  // (window.__fizzyVariants: [{id, price(cents), compare(cents), available, sp, options[]}]).
  // Option matching is whitespace/case-insensitive so the same code runs on the
  // dev store ("Watermelon") and production ("Water Melon"). Line-item property
  // values always use the STORE's exact option string (storeOptionName), since
  // the production bundle app matches on them.
  var FLAVOR_OPT = { bc: 'Berry Cherry', sl: 'Strawberry Lemonade', wm: 'Water Melon', ob: 'Orange Creamsicle' };
  var AGE_OPT = { '8-13': 'Ages 8-13', '14+': 'Ages 14+' };
  var PLAN_OPT = { '1m': '1 Month Supply', '2m': '2 Month Supply', '3m': '3 Month Supply', 'once': 'Try It Once' };
  // Last-resort Skio selling-plan ids (production). The injected per-variant
  // `sp` (from selling_plan_allocations) is preferred — store-independent; on
  // the dev store (no Skio) sp is null and the add is a clean one-time.
  var SELLING_PLAN = { '1m': 11638964535, '2m': 11917263159, '3m': 11917295927 };
  function money(cents) { var d = (cents || 0) / 100; return '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2)); }
  // HTML-escape — defined at the IIFE scope so the outer-scope builders
  // (slotsMarkup, variantRowsMarkup) can use it too. There's a second esc
  // inside init() for the screen builders; this one covers everything else.
  // (Fix 2026-06-10: outcome step threw "Can't find variable: esc".)
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function normOpt(s) { return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9+]/g, ''); }
  function variantFor(fp, ageTier, pp) {
    var list = window.__fizzyVariants || [];
    var f = normOpt(FLAVOR_OPT[fp]), a = normOpt(AGE_OPT[ageTier]), pl = normOpt(PLAN_OPT[pp]);
    for (var i = 0; i < list.length; i++) {
      var o = (list[i].options || []).map(normOpt);
      if (o.indexOf(f) !== -1 && o.indexOf(a) !== -1 && o.indexOf(pl) !== -1) return list[i];
    }
    return null;
  }
  // The store's exact option string for a flavor param (e.g. prod "Water Melon"
  // vs dev "Watermelon") — written into Flavor 1..N properties.
  function storeOptionName(fp) {
    var list = window.__fizzyVariants || [], want = normOpt(FLAVOR_OPT[fp]);
    for (var i = 0; i < list.length; i++) {
      var o = list[i].options || [];
      for (var j = 0; j < o.length; j++) { if (normOpt(o[j]) === want) return o[j]; }
    }
    return FLAVOR_OPT[fp];
  }

  /* ── Flow definition ─────────────────────────────────────────────────
     type: 'single' (one-select), 'multi' (multi-select, ranked by pick
     order), 'info' (interstitial), 'intro', 'quote' (testimonial),
     'outcome'. A step with showIf(state) is skipped when it returns false. */
  var STEPS = [
    {
      id: 'audience', type: 'single',
      q: 'Who is this for?', sub: 'Select one',
      options: [
        { v: 'child', label: 'My child' },
        { v: 'teen', label: 'A teen in my family' },
        { v: 'self', label: 'Myself' },
        { v: 'exploring', label: 'Just exploring' }
      ]
    },
    {
      id: 'intro', type: 'intro',
      q: 'Introducing FizzyFocus.',
      image: 'https://cdn.shopify.com/s/files/1/0631/8915/5937/files/berry10.png?v=1780526853',
      benefits: ['Smooth focus, no crash', 'Calmer mood & attention', 'Fills everyday nutrient gaps'],
      disclaimer: function (a) {
        var pro = (a === 'self' || a === 'exploring') ? 'doctor' : 'pediatrician';
        return "We make evidence-based recommendations from current research — we don't diagnose or treat any condition, or give medical advice. Check with your " + pro + " before starting a new supplement.";
      }
    },
    {
      id: 'age', type: 'single',
      q: function (a) {
        if (a === 'teen') return 'How old is your teen?';
        if (a === 'self' || a === 'exploring') return 'How old are you?';
        return 'How old is your child?';
      },
      options: function (a) {
        if (a === 'self' || a === 'exploring') {
          return [ { v: '18-29', label: '18–29' }, { v: '30-44', label: '30–44' }, { v: '45-59', label: '45–59' }, { v: '60+', label: '60+' } ];
        }
        if (a === 'teen') {
          return [ { v: '11-13', label: '11–13' }, { v: '14-17', label: '14–17' } ];
        }
        return [ { v: '4-7', label: '4–7' }, { v: '8-10', label: '8–10' }, { v: '11-13', label: '11–13' }, { v: '14-17', label: '14–17' } ];
      }
    },
    {
      id: 'priority', type: 'multi',
      q: 'What are you hoping FizzyFocus will help with most?',
      sub: 'Pick all that apply.',
      options: function (a) {
        var adult = (a === 'self' || a === 'exploring');
        return [
          { v: 'focus', label: adult ? 'Better focus at work or study' : 'Better focus during schoolwork' },
          { v: 'energy', label: 'Steadier mornings and afternoon energy' },
          { v: 'mood', label: 'Calmer mood and less irritability' },
          { v: 'gaps', label: adult ? 'Filling everyday nutrient gaps' : 'Filling nutritional gaps (picky eater)' }
        ];
      }
    },
    {
      id: 'stimulant', type: 'single',
      q: function (a) {
        if (a === 'self' || a === 'exploring') return 'Are you using a stimulant medication right now?';
        if (a === 'teen') return 'Is your teen using a stimulant medication right now?';
        return 'Is your child using a stimulant medication right now?';
      },
      sub: 'This just helps us show what’s most relevant.',
      options: function (a) {
        var adult = (a === 'self' || a === 'exploring');
        return [
          { v: 'now', label: adult ? 'Yes, I take one now' : 'Yes, they take one now' },
          { v: 'tried', label: adult ? 'I’ve tried one before' : 'We’ve tried one before' },
          { v: 'close', label: adult ? 'Someone close to me does' : 'Someone close to us does' },
          { v: 'no', label: 'No — and not looking into it' }
        ];
      }
    },
    {
      // Surfaced only when the answer above shows stimulant familiarity.
      // A customer review that names the downside they know, and how
      // FizzyFocus helps. NOTE: placeholder testimonial — swap for a real
      // approved review. Claims are anecdotal (customer voice), and the
      // brand-voice `note` stays nutritional + within the caffeine ruling.
      id: 'stimproof', type: 'quote',
      showIf: function (s) { return s.stimulant && s.stimulant !== 'no'; },
      eyebrow: 'From a FizzyIQ family',
      q: 'The part you already know about.',
      quote: function (a) {
        return (a === 'self' || a === 'exploring')
          ? '“The crash and the appetite dip were the worst parts of my meds. Adding FizzyFocus smoothed out my afternoons — steady focus, no hard comedown, and no jitters.”'
          : '“The afternoon crash and skipped meals were the hardest part of stimulant days. With FizzyFocus alongside, the evenings are calmer, his appetite came back, and it’s a drink he actually asks for.”';
      },
      author: function (a) {
        return (a === 'self' || a === 'exploring') ? 'Jordan T. · FizzyFocus member' : 'Megan R. · parent of an 8-year-old';
      },
      note: 'Smooth focus with no crash, everyday nutrient gaps filled, and only green-tea caffeine paired with L-theanine — no artificial stimulants.'
    },
    {
      id: 'how', type: 'info',
      eyebrow: 'How it works',
      q: 'How FizzyFocus actually works',
      points: function (a) {
        var gap = (a === 'self' || a === 'exploring')
          ? 'people who struggle to focus often run lower in key nutrients'
          : 'kids who struggle to focus tend to run lower in ones';
        return [
          { ic: 'leaf', h: 'Built around real gaps', p: 'Focus, mood, and attention run on specific nutrients — and studies repeatedly find that ' + gap + ' like magnesium and zinc.' },
          { ic: 'target', h: 'Targets the root, not just symptoms', p: 'FizzyFocus fills those gaps in active, bioavailable forms the body can actually take up — which matters even more when the gut-brain connection is sensitive.' },
          { ic: 'beaker', h: 'Ingredients that work together', p: "Several nutrients are paired specifically to boost each other's absorption, so more of every dose is actually used — not just swallowed." },
          { ic: 'bolt', h: 'Smooth focus, no crash', p: 'Real focus support usually needs some stimulation — so rather than a synthetic stimulant, we use naturally-sourced green-tea caffeine with L-theanine. It’s one of the most-studied pairings for steady attention without the jitters or the crash.' }
        ];
      }
    },
    {
      id: 'feeling', type: 'single',
      q: function (a) {
        if (a === 'teen') return 'How does your teen feel about supplements right now?';
        if (a === 'self' || a === 'exploring') return 'How do you feel about supplements right now?';
        return 'How does your child feel about supplements right now?';
      },
      options: function (a) {
        if (a === 'self' || a === 'exploring') {
          return [
            { v: 'loves', label: 'I love them' },
            { v: 'okay', label: "I'm fine with them" },
            { v: 'resists', label: 'I tend to skip them' },
            { v: 'none', label: "I haven't tried any" }
          ];
        }
        return [
          { v: 'loves', label: 'Loves them' },
          { v: 'okay', label: 'Okay with them' },
          { v: 'resists', label: 'Tends to resist them' },
          { v: 'none', label: "Hasn't tried any" }
        ];
      }
    },
    {
      id: 'flavor', type: 'multi',
      q: function (a) { return (a === 'self' || a === 'exploring') ? 'Which flavors do you like?' : 'Which flavors usually land with them?'; },
      sub: 'Pick all that work — tap your favorite first.',
      options: function () {
        return [
          { v: 'bc', label: 'Berry Cherry' },
          { v: 'sl', label: 'Strawberry Lemonade' },
          { v: 'wm', label: 'Watermelon' },
          { v: 'ob', label: 'Orange Creamsicle' }
        ];
      }
    },
    { id: 'outcome', type: 'outcome' }
  ];

  /* ── Product data (mirrors the PDP buy box) ──────────────────────────── */
  var ALL_FLAVORS = [
    { param: 'bc', name: 'Berry Cherry' },
    { param: 'sl', name: 'Strawberry Lemonade' },
    { param: 'wm', name: 'Watermelon' },
    { param: 'ob', name: 'Orange Creamsicle' }
  ];
  var FLAVOR_IMG = {
    bc: 'https://cdn.shopify.com/s/files/1/0631/8915/5937/files/berry10.png?v=1780526853',
    sl: 'https://cdn.shopify.com/s/files/1/0631/8915/5937/files/strawberry10.png?v=1780526815',
    wm: 'https://cdn.shopify.com/s/files/1/0631/8915/5937/files/watermelon10.png?v=1780526855',
    ob: 'https://cdn.shopify.com/s/files/1/0631/8915/5937/files/orange10.png?v=1780526858'
  };
  // Front-pouch shots for the bundle "slots" (one per flavor).
  var POUCH_IMG = {
    bc: 'https://cdn.shopify.com/s/files/1/0631/8915/5937/files/Fizzy_IQ_Berry_Cherry_Front.png?v=1779128402&width=400',
    sl: 'https://cdn.shopify.com/s/files/1/0631/8915/5937/files/Fizzy_IQ_Strawberry_Lemonade_Front.png?v=1779128398&width=400',
    wm: 'https://cdn.shopify.com/s/files/1/0631/8915/5937/files/FizzyIQ_Watermelon_Front_2.png?v=1779128400&width=400',
    ob: 'https://cdn.shopify.com/s/files/1/0631/8915/5937/files/Fizzy_IQ_Orange_Creamsicle_Front.png?v=1779128395&width=400'
  };
  var DEFAULT_FLAVOR = 'bc';
  // Plan tiers + prices mirror fizzy-pdp-hero.liquid exactly.
  var ALL_PLANS = [
    { param: '1m', name: 'Monthly Delivery',  sub: '30 servings · free shipping', prop: 'Monthly Delivery' },
    { param: '2m', name: '2-month Delivery',  sub: '60 servings · free shipping', prop: '2-month Delivery' },
    { param: '3m', name: '3-month Delivery',  sub: '90 servings · free shipping', prop: '3-month Delivery' }
  ];
  var DEFAULT_PLAN = '1m';           // monthly = the standard, pre-selected option
  var RECOMMENDED_PLAN = '2m';       // 2-month carries the "Recommended" badge (Sam 2026-06-08)
  var POUCHES = { '1m': 1, '2m': 2, '3m': 3 };   // plan length in months
  // Ages 14+ take 2 servings/day → 2 pouches (= 2 flavor slots) per plan-month.
  function slotsFor(plan, age) { return (POUCHES[plan] || 1) * (age === '14+' ? 2 : 1); }
  function servingsFor(plan, age) { return slotsFor(plan, age) * 30; }
  function flavorName(param) { for (var i = 0; i < ALL_FLAVORS.length; i++) { if (ALL_FLAVORS[i].param === param) return ALL_FLAVORS[i].name; } return 'Berry Cherry'; }
  function planByParam(param) { for (var i = 0; i < ALL_PLANS.length; i++) { if (ALL_PLANS[i].param === param) return ALL_PLANS[i]; } return ALL_PLANS[0]; }
  function totalQty(qty) { var t = 0; for (var i = 0; i < ALL_FLAVORS.length; i++) t += (qty[ALL_FLAVORS[i].param] || 0); return t; }
  function primaryFlavor(qty) { for (var i = 0; i < ALL_FLAVORS.length; i++) { if ((qty[ALL_FLAVORS[i].param] || 0) > 0) return ALL_FLAVORS[i].param; } return DEFAULT_FLAVOR; }
  function flavorSummary(qty) { var parts = []; for (var i = 0; i < ALL_FLAVORS.length; i++) { var f = ALL_FLAVORS[i], n = qty[f.param] || 0; if (n > 0) parts.push(f.name + (n > 1 ? ' ×' + n : '')); } return parts.join(' · '); }
  // Expand a qty map into a flat per-unit list (one entry per pouch in the bundle).
  function expandUnits(qty) { var u = []; for (var i = 0; i < ALL_FLAVORS.length; i++) { var f = ALL_FLAVORS[i]; for (var k = 0; k < (qty[f.param] || 0); k++) u.push(f); } return u; }
  // Bundle "slots" — exactly N (one per pouch the bundle holds), filled unit-by-unit.
  function slotsMarkup(qty, N) {
    var u = expandUnits(qty), h = '';
    for (var s = 0; s < N; s++) {
      var f = u[s];
      h += f
        ? '<div class="fq-slot is-filled" data-slot-idx="' + s + '"><img class="fq-slot-img" src="' + esc(POUCH_IMG[f.param]) + '" alt="' + esc(f.name) + '" loading="lazy"></div>'
        : '<div class="fq-slot" data-slot-idx="' + s + '"></div>';
    }
    return h;
  }
  function slotCols(N) { return N === 4 ? 2 : Math.min(N, 3); }
  // Bundle summary — one row per selected flavor (name + ×qty), plus a unit count.
  function variantRowsMarkup(qty) {
    var h = '';
    for (var i = 0; i < ALL_FLAVORS.length; i++) { var f = ALL_FLAVORS[i], n = qty[f.param] || 0; if (n > 0) h += '<li class="fq-bv"><span class="fq-bv-name">' + esc(f.name) + '</span><span class="fq-bv-qty">×' + n + '</span></li>'; }
    return h;
  }
  function unitsLabel(t) { return t + (t === 1 ? ' pouch' : ' pouches'); }

  var ICONS = {
    leaf: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 4S8 4 6 12c-1 4 1 7 1 7s3-9 12-11c0 0-7 3-9 9 0 0 6 1 9-3 3-4 1-10 1-10z"/></svg>',
    beaker: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 3h6M10 3v6L5 19a1 1 0 0 0 .9 1.5h12.2A1 1 0 0 0 19 19l-5-10V3"/><path d="M7.5 14h9"/></svg>',
    bolt: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 2 4 14h6l-1 8 9-12h-6z"/></svg>',
    shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2 4 5v6c0 5 3.4 7.7 8 9 4.6-1.3 8-4 8-9V5l-8-3z"/><path d="m9 12 2 2 4-4"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="m8.5 12 2.4 2.4 4.6-5"/></svg>',
    target: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4.5"/><circle cx="12" cy="12" r="0.6" fill="currentColor"/></svg>',
    quote: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 7h4v4c0 3-2 5-5 5v-2c2 0 3-1 3-3H7V7zm8 0h4v4c0 3-2 5-5 5v-2c2 0 3-1 3-3h-2V7z"/></svg>'
  };

  function boot() {
    var root = document.getElementById('fizzyQuiz');
    if (!root) return;
    var stage = root.querySelector('#fqStage');
    var backBtn = root.querySelector('[data-fq-back]');
    var progress = root.querySelector('.fq-progress');

    var state = {};        // { audience, age, priority, stimulant, feeling, flavor:[...] }
    var current = 0;
    var lastFocus = null;
    var capturedEmail = '';   // set when the email gate is submitted on the outcome

    function isVisible(step) { return !step.showIf || step.showIf(state); }

    function paintProgress() {
      if (!progress) return;
      var total = 0, i;
      for (i = 0; i < STEPS.length; i++) { if (isVisible(STEPS[i]) && STEPS[i].type !== 'outcome') total++; }
      var pos = 0;
      for (i = 0; i < STEPS.length && i <= current; i++) { if (isVisible(STEPS[i]) && STEPS[i].type !== 'outcome') pos++; }
      if (STEPS[current].type === 'outcome') pos = total;
      var segs = progress.querySelectorAll('.fq-progress-seg');
      if (segs.length !== total) {
        var frag = '';
        for (i = 0; i < total; i++) frag += '<span class="fq-progress-seg"></span>';
        progress.innerHTML = frag;
        segs = progress.querySelectorAll('.fq-progress-seg');
      }
      Array.prototype.forEach.call(segs, function (s, idx) { s.classList.toggle('is-done', idx < pos); });
    }

    function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

    // Resolve a value that may be audience-aware (a function of the audience).
    function resolve(v) { return typeof v === 'function' ? v(state.audience || 'child') : v; }

    function firstFlavor() { return (state.flavor && state.flavor.length) ? state.flavor[0] : DEFAULT_FLAVOR; }
    // 2 servings/day for teens 14+ and adults; 1 serving/day otherwise.
    function ageTier() {
      var a = state.age || '';
      return (a === '14-17' || a === '18-29' || a === '30-44' || a === '45-59' || a === '60+') ? '14+' : '8-13';
    }
    function ageLabel(t) { return t === '14+' ? 'Ages 14+' : 'Ages 8–13'; }

    /* ── Screen builders ─────────────────────────────────────────────── */
    function buildSingle(step) {
      var selected = state[step.id];
      var q = resolve(step.q);
      var sub = resolve(step.sub) || 'Select one';
      var options = resolve(step.options);
      var opts = options.map(function (o) {
        var on = selected === o.v;
        return '<button type="button" class="fq-opt' + (on ? ' is-selected' : '') + '" data-value="' + esc(o.v) + '" aria-pressed="' + on + '">' + esc(o.label) + '</button>';
      }).join('');
      return '' +
        '<div class="fq-screen">' +
          '<h2 class="fq-q">' + esc(q) + '</h2>' +
          '<p class="fq-sub">' + esc(sub) + '</p>' +
          '<div class="fq-options">' + opts + '</div>' +
        '</div>';
    }
    function buildMulti(step) {
      var sel = state[step.id] || [];
      var q = resolve(step.q);
      var sub = resolve(step.sub) || 'Pick all that apply';
      var options = resolve(step.options);
      var opts = options.map(function (o) {
        var idx = sel.indexOf(o.v);
        var on = idx >= 0;
        return '<button type="button" class="fq-opt fq-opt--multi' + (on ? ' is-selected' : '') + '" data-value="' + esc(o.v) + '" aria-pressed="' + on + '">' +
          (on ? '<span class="fq-opt-rank">' + (idx + 1) + '</span>' : '') + esc(o.label) + '</button>';
      }).join('');
      return '' +
        '<div class="fq-screen">' +
          '<h2 class="fq-q">' + esc(q) + '</h2>' +
          '<p class="fq-sub">' + esc(sub) + '</p>' +
          '<div class="fq-options fq-options--multi">' + opts + '</div>' +
          '<button type="button" class="fq-cta" data-fq-continue' + (sel.length ? '' : ' disabled') + '>Continue</button>' +
        '</div>';
    }
    function buildInfo(step) {
      var items = resolve(step.points).map(function (p) {
        return '<li class="fq-info-item"><span class="fq-info-ico">' + (ICONS[p.ic] || '') + '</span>' +
          '<span><span class="fq-info-h">' + esc(p.h) + '</span><span class="fq-info-p">' + esc(p.p) + '</span></span></li>';
      }).join('');
      return '' +
        '<div class="fq-screen">' +
          '<p class="fq-info-eyebrow">' + esc(step.eyebrow) + '</p>' +
          '<h2 class="fq-q">' + esc(step.q) + '</h2>' +
          '<ul class="fq-info-list">' + items + '</ul>' +
          '<button type="button" class="fq-cta" data-fq-continue>Continue</button>' +
        '</div>';
    }
    function buildQuote(step) {
      return '' +
        '<div class="fq-screen">' +
          '<p class="fq-info-eyebrow">' + esc(resolve(step.eyebrow)) + '</p>' +
          '<h2 class="fq-q">' + esc(resolve(step.q)) + '</h2>' +
          '<figure class="fq-quote-card">' +
            '<span class="fq-quote-mark" aria-hidden="true">' + ICONS.quote + '</span>' +
            '<blockquote class="fq-quote-text">' + esc(resolve(step.quote)) + '</blockquote>' +
            '<figcaption class="fq-quote-author">' + esc(resolve(step.author)) + '</figcaption>' +
          '</figure>' +
          (step.note ? '<p class="fq-quote-note">' + esc(resolve(step.note)) + '</p>' : '') +
          '<button type="button" class="fq-cta" data-fq-continue>Continue</button>' +
        '</div>';
    }
    function buildIntro(step) {
      var benefits = step.benefits.map(function (b) {
        return '<li class="fq-intro-benefit"><span class="fq-intro-check">' + ICONS.check + '</span>' + esc(b) + '</li>';
      }).join('');
      return '' +
        '<div class="fq-screen fq-screen--intro">' +
          '<h2 class="fq-q fq-intro-h">' + esc(step.q) + '</h2>' +
          '<div class="fq-intro-card">' +
            '<div class="fq-intro-media"><img src="' + esc(step.image) + '" alt="FizzyFocus" loading="lazy"></div>' +
            '<ul class="fq-intro-benefits">' + benefits + '</ul>' +
          '</div>' +
          '<button type="button" class="fq-cta" data-fq-continue>Continue</button>' +
          (resolve(step.disclaimer) ?
            '<div class="fq-intro-disc"><p class="fq-intro-disc-h">Disclaimer</p><p class="fq-intro-disc-p">' + esc(resolve(step.disclaimer)) + '</p></div>' : '') +
        '</div>';
    }
    function buildOutcome() {
      var recFlavor = firstFlavor();
      var recPlan = DEFAULT_PLAN;
      var recAge = ageTier();
      var adultAud = (state.audience === 'self' || state.audience === 'exploring');
      var ageWord = state.age ? ('For ages ' + state.age.replace('-', '–') + ', ') : '';
      var prMap = {
        focus: adultAud ? 'focused on work or study' : 'focused on schoolwork',
        energy: 'focused on steadier energy',
        mood: 'focused on calmer mood',
        gaps: 'filling nutrient gaps'
      };
      var prList = Array.isArray(state.priority) ? state.priority : (state.priority ? [state.priority] : []);
      var priorityLine = prList.length > 1 ? 'covering a few priorities'
        : (prList.length === 1 ? (prMap[prList[0]] || 'getting started') : 'getting started');

      // Seed the flavor allocation across the plan's pouch slots from the quiz picks.
      var N0 = slotsFor(recPlan, recAge);
      var picks = (state.flavor && state.flavor.length) ? state.flavor.slice() : [recFlavor];
      var qty0 = {};
      for (var qi = 0; qi < ALL_FLAVORS.length; qi++) qty0[ALL_FLAVORS[qi].param] = 0;
      for (var pi = 0; pi < picks.length && pi < N0; pi++) qty0[picks[pi]]++;
      while (totalQty(qty0) < N0) qty0[picks[0]]++;
      var primary0 = primaryFlavor(qty0);

      var ageOpts = [ { v: '8-13', n: 'Ages 8–13', h: '1 serving daily' }, { v: '14+', n: 'Ages 14+', h: '2 servings daily' } ].map(function (o) {
        var on = o.v === recAge;
        return '<button type="button" class="fq-age-opt' + (on ? ' is-selected' : '') + '" data-age="' + o.v + '" aria-pressed="' + on + '">' +
          '<span class="fq-age-n">' + o.n + '</span><span class="fq-age-h">' + o.h + '</span></button>';
      }).join('');

      var flavorRows = ALL_FLAVORS.map(function (f) {
        return '<div class="fq-bld-flavor" data-flavor="' + f.param + '">' +
          '<span class="fq-bld-name">' + esc(f.name) + '</span>' +
          '<span class="fq-bld-stepper">' +
            '<button type="button" class="fq-bld-step" data-dec aria-label="Fewer ' + esc(f.name) + '">–</button>' +
            '<span class="fq-bld-qty" data-qty>' + (qty0[f.param] || 0) + '</span>' +
            '<button type="button" class="fq-bld-step" data-inc aria-label="More ' + esc(f.name) + '">+</button>' +
          '</span>' +
        '</div>';
      }).join('');

      var plans = ALL_PLANS.map(function (p) {
        var on = p.param === recPlan;
        var v = variantFor(primary0, recAge, p.param);
        return '<button type="button" class="fq-plan' + (on ? ' is-selected' : '') + '" data-plan="' + p.param + '" aria-pressed="' + on + '">' +
          '<span class="fq-plan-main"><span class="fq-plan-name">' + esc(p.name) + '</span><span class="fq-plan-sub">' + esc(servingsFor(p.param, recAge) + ' servings · free shipping') + '</span></span>' +
          '<span class="fq-plan-price"><span class="fq-plan-now">' + (v ? money(v.price) : '') + '</span></span>' +
          (p.param === RECOMMENDED_PLAN ? '<span class="fq-rec-badge fq-rec-badge--plan">Recommended</span>' : '') + '</button>';
      }).join('');

      var together = (state.audience === 'child' || state.audience === 'teen') ?
        ('<div class="fq-together">' +
          '<span class="fq-together-ico" aria-hidden="true">' + ICONS.check + '</span>' +
          '<div><p class="fq-together-label">Better together</p>' +
          '<p class="fq-together-text">Kids and teens whose parents take FizzyFocus alongside them are <strong>90% more likely</strong> to stay consistent and see results.</p></div>' +
        '</div>') : '';

      var v0 = variantFor(primary0, recAge, recPlan);
      var sumAge = ageLabel(recAge) + ' · ' + (recAge === '14+' ? '2 servings daily' : '1 serving daily');
      var slotsHtml = slotsMarkup(qty0, N0);
      return '' +
        '<div class="fq-screen">' +
          '<p class="fq-out-eyebrow">Your plan</p>' +
          '<h2 class="fq-q">Here’s where we’d start.</h2>' +
          '<p class="fq-sub" style="color:rgba(10,19,36,0.62)">' + esc(ageWord + priorityLine) + ' — enter your email and we’ll show your bundle + 10% off.</p>' +
          /* Phase 1 — capture email first. */
          '<div class="fq-gate" data-fq-gate>' +
            '<div class="fq-email">' +
              '<label class="fq-email-label" for="fqEmail">Your email</label>' +
              '<input class="fq-email-input" id="fqEmail" type="email" inputmode="email" autocomplete="email" placeholder="you@email.com" required>' +
            '</div>' +
            '<button type="button" class="fq-cta" data-fq-reveal disabled>See my plan</button>' +
            '<p class="fq-fineprint">We’ll email your code and auto-apply 10% off (QUIZ10) at checkout. No spam — unsubscribe anytime.</p>' +
          '</div>' +
          /* Phase 2 — recommendations, revealed after email. */
          '<div class="fq-recs" data-fq-recs hidden>' +
            together +
            '<div class="fq-bundle" data-fq-bundle>' +
              '<div class="fq-bundle-top">' +
                '<div class="fq-bundle-media"><div class="fq-bundle-slots" style="grid-template-columns:repeat(' + slotCols(N0) + ',1fr)">' + slotsHtml + '</div></div>' +
                '<div class="fq-bundle-summary">' +
                  '<div class="fq-bundle-head"><span class="fq-bundle-label">Your bundle</span>' +
                    '<button type="button" class="fq-bundle-toggle" data-fq-bundle-toggle aria-expanded="false">Edit<svg class="fq-bundle-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg></button>' +
                  '</div>' +
                  '<ul class="fq-bundle-variants" data-sum-variants>' + variantRowsMarkup(qty0) + '</ul>' +
                  '<p class="fq-bundle-meta"><span data-sum-units>' + unitsLabel(totalQty(qty0)) + '</span> · <span data-sum-plan>' + esc(planByParam(recPlan).name) + '</span> · <span data-sum-age>' + esc(sumAge) + '</span></p>' +
                  '<p class="fq-bundle-price"><span class="fq-bundle-now" data-sum-now>' + (v0 ? money(v0.price) : '') + '</span> <span class="fq-bundle-disc">extra 10% with QUIZ10</span></p>' +
                '</div>' +
              '</div>' +
              '<div class="fq-bundle-body">' +
                '<div class="fq-pick"><p class="fq-pick-label">Age</p><div class="fq-age-opts">' + ageOpts + '</div></div>' +
                '<div class="fq-pick"><p class="fq-pick-label">Flavors <span class="fq-slot-count" data-slot-count></span></p><div class="fq-bld-flavors">' + flavorRows + '</div></div>' +
                '<div class="fq-pick"><p class="fq-pick-label">Plan</p><div class="fq-plans">' + plans + '</div></div>' +
              '</div>' +
            '</div>' +
            '<div class="fq-upsell" data-fq-upsell></div>' +
            '<button type="button" class="fq-cta" data-fq-finish>Add my bundle</button>' +
            '<p class="fq-fineprint">10% off (QUIZ10) auto-applies at checkout.</p>' +
          '</div>' +
        '</div>';
    }

    function buildScreen(step) {
      if (step.type === 'info') return buildInfo(step);
      if (step.type === 'intro') return buildIntro(step);
      if (step.type === 'quote') return buildQuote(step);
      if (step.type === 'multi') return buildMulti(step);
      if (step.type === 'outcome') return buildOutcome();
      return buildSingle(step);
    }

    /* ── Render with cross-fade ──────────────────────────────────────── */
    function render(animate) {
      var step = STEPS[current];
      var html = buildScreen(step);
      var prev = stage.querySelector('.fq-screen');
      function swap() {
        stage.innerHTML = html;
        wire(step);
        paintProgress();
        if (backBtn) backBtn.hidden = false;   // always available
        var firstFocusable = stage.querySelector('.fq-opt, .fq-email-input, .fq-cta');
        if (firstFocusable && animate) { try { firstFocusable.focus({ preventScroll: true }); } catch (e) {} }
        var body = root.querySelector('.fq-body');
        if (body) body.scrollTop = 0;
      }
      if (prev && animate) {
        prev.classList.add('is-leaving');
        setTimeout(swap, 240);
      } else {
        swap();
      }
    }

    function wire(step) {
      if (step.type === 'single') {
        // Auto-advance: picking an answer moves to the next question (Sam 2026-06-07).
        var advancing = false;
        Array.prototype.forEach.call(stage.querySelectorAll('.fq-opt'), function (btn) {
          btn.addEventListener('click', function () {
            if (advancing) return;
            state[step.id] = btn.getAttribute('data-value');
            Array.prototype.forEach.call(stage.querySelectorAll('.fq-opt'), function (b) {
              var on = b === btn; b.classList.toggle('is-selected', on); b.setAttribute('aria-pressed', on);
            });
            advancing = true;
            setTimeout(advance, 200);   // brief beat so the selection registers, then advance
          });
        });
      } else if (step.type === 'multi') {
        var mcta = stage.querySelector('[data-fq-continue]');
        if (!Array.isArray(state[step.id])) state[step.id] = [];
        var sel = state[step.id];
        function repaint() {
          Array.prototype.forEach.call(stage.querySelectorAll('.fq-opt'), function (b) {
            var v = b.getAttribute('data-value');
            var idx = sel.indexOf(v); var on = idx >= 0;
            b.classList.toggle('is-selected', on); b.setAttribute('aria-pressed', on);
            var rank = b.querySelector('.fq-opt-rank');
            if (on) {
              if (!rank) { rank = document.createElement('span'); rank.className = 'fq-opt-rank'; b.insertBefore(rank, b.firstChild); }
              rank.textContent = idx + 1;
            } else if (rank) { rank.parentNode.removeChild(rank); }
          });
          if (mcta) mcta.disabled = sel.length === 0;
        }
        Array.prototype.forEach.call(stage.querySelectorAll('.fq-opt'), function (btn) {
          btn.addEventListener('click', function () {
            var v = btn.getAttribute('data-value');
            var i = sel.indexOf(v);
            if (i >= 0) sel.splice(i, 1); else sel.push(v);
            repaint();
          });
        });
        if (mcta) mcta.addEventListener('click', advance);
      } else if (step.type === 'info' || step.type === 'intro' || step.type === 'quote') {
        var c = stage.querySelector('[data-fq-continue]');
        if (c) c.addEventListener('click', advance);
      } else if (step.type === 'outcome') {
        var chosenAge = ageTier();
        var chosenPlan = DEFAULT_PLAN;
        var qty = {};
        var bundle = stage.querySelector('[data-fq-bundle]');
        var toggle = stage.querySelector('[data-fq-bundle-toggle]');
        var addBtn = stage.querySelector('[data-fq-finish]');
        // Seed the live qty from the rendered steppers, then normalise against
        // current stock (drop sold-out picks, refill from in-stock flavors).
        Array.prototype.forEach.call(stage.querySelectorAll('.fq-bld-flavor'), function (row) {
          qty[row.getAttribute('data-flavor')] = parseInt(row.querySelector('[data-qty]').textContent, 10) || 0;
        });
        clampQty();
        if (toggle && bundle) toggle.addEventListener('click', function () {
          var open = bundle.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
        function setText(sel, txt) { var el = stage.querySelector(sel); if (el) el.textContent = txt; }
        function setPlan(pp) { chosenPlan = pp; clampQty(); refreshAll(); }
        // A flavor is buyable only if its bundle variant for the current age +
        // plan is in stock. Shopify computes that availability from the child
        // SKU, so reading variant.available reflects true stock (frontend only).
        function avail(fp) { var v = variantFor(fp, chosenAge, chosenPlan); return !!(v && v.available); }
        function clampQty() {
          var N = slotsFor(chosenPlan, chosenAge), i, fp;
          // Drop any flavor that's sold out for the current age + plan.
          for (i = 0; i < ALL_FLAVORS.length; i++) { if (!avail(ALL_FLAVORS[i].param)) qty[ALL_FLAVORS[i].param] = 0; }
          // Trim if over the slot count.
          for (i = ALL_FLAVORS.length - 1; i >= 0 && totalQty(qty) > N; i--) {
            fp = ALL_FLAVORS[i].param;
            while ((qty[fp] || 0) > 0 && totalQty(qty) > N) qty[fp]--;
          }
          // Refill remaining slots from the first in-stock flavor (if any).
          var avs = ALL_FLAVORS.filter(function (f) { return avail(f.param); });
          if (avs.length) { while (totalQty(qty) < N) qty[avs[0].param] = (qty[avs[0].param] || 0) + 1; }
        }
        function refreshAll() {
          var N = slotsFor(chosenPlan, chosenAge);
          // Drop anything that went out of stock for the current age + plan.
          ALL_FLAVORS.forEach(function (f) { if (qty[f.param] && !avail(f.param)) qty[f.param] = 0; });
          var t = totalQty(qty), primary = primaryFlavor(qty);
          var vSel = variantFor(primary, chosenAge, chosenPlan);
          var vlist = stage.querySelector('[data-sum-variants]'); if (vlist) vlist.innerHTML = variantRowsMarkup(qty);
          // All flavors sold out → collapse the empty pouch-slot well + price
          // (CSS keys off fq-bundle--empty) so the card reads as out-of-stock.
          if (bundle) bundle.classList.toggle('fq-bundle--empty', t === 0);
          setText('[data-sum-units]', t === 0 ? 'Out of stock' : unitsLabel(t));
          setText('[data-sum-plan]', planByParam(chosenPlan).name);
          setText('[data-sum-age]', ageLabel(chosenAge) + ' · ' + (chosenAge === '14+' ? '2 servings daily' : '1 serving daily'));
          setText('[data-sum-now]', vSel ? money(vSel.price) : '');
          var slotsWrap = stage.querySelector('.fq-bundle-slots');
          if (slotsWrap) { slotsWrap.style.gridTemplateColumns = 'repeat(' + slotCols(N) + ',1fr)'; slotsWrap.innerHTML = slotsMarkup(qty, N); }
          setText('[data-slot-count]', '· ' + t + ' of ' + N + ' selected');
          Array.prototype.forEach.call(stage.querySelectorAll('.fq-bld-flavor'), function (row) {
            var fp = row.getAttribute('data-flavor'), n = qty[fp] || 0, soldOut = !avail(fp);
            row.querySelector('[data-qty]').textContent = n;
            row.querySelector('[data-dec]').disabled = n <= 0;
            row.querySelector('[data-inc]').disabled = t >= N || soldOut;
            row.classList.toggle('is-active', n > 0);
            row.classList.toggle('fq-bld-flavor--soldout', soldOut);
          });
          Array.prototype.forEach.call(stage.querySelectorAll('.fq-plan'), function (btn) {
            var pp = btn.getAttribute('data-plan'), v = variantFor(primary, chosenAge, pp), on = pp === chosenPlan;
            var nowEl = btn.querySelector('.fq-plan-now'); if (nowEl) nowEl.textContent = v ? money(v.price) : '';
            var subEl = btn.querySelector('.fq-plan-sub'); if (subEl) subEl.textContent = servingsFor(pp, chosenAge) + ' servings · free shipping';
            btn.classList.toggle('is-selected', on); btn.setAttribute('aria-pressed', on);
          });
          var upsell = stage.querySelector('[data-fq-upsell]');
          if (upsell) {
            var order = ['1m', '2m', '3m'], months = { '2m': 2, '3m': 3 };
            var one = variantFor(primary, chosenAge, '1m'), html = '';
            for (var oi = order.indexOf(chosenPlan) + 1; oi < order.length; oi++) {
              var pp2 = order[oi], pv = variantFor(primary, chosenAge, pp2), pl = planByParam(pp2);
              if (!pv) continue;
              var save = (one ? one.price * months[pp2] : 0) - pv.price;
              html += '<button type="button" class="fq-up" data-plan="' + pp2 + '">' +
                '<span class="fq-up-main"><span class="fq-up-name">Upgrade to ' + esc(pl.name) + '</span><span class="fq-up-sub">' + esc(servingsFor(pp2, chosenAge) + ' servings') + ' · ' + money(pv.price) + '</span></span>' +
                (save > 0 ? '<span class="fq-up-save">Save ' + money(save) + '</span>' : '') + '</button>';
            }
            upsell.innerHTML = html;
            Array.prototype.forEach.call(upsell.querySelectorAll('.fq-up'), function (b) {
              b.addEventListener('click', function () { setPlan(b.getAttribute('data-plan')); });
            });
          }
          if (addBtn) {
            var anyAvail = ALL_FLAVORS.some(function (f) { return avail(f.param); });
            addBtn.disabled = (t !== N) || !anyAvail;
            addBtn.textContent = anyAvail ? 'Add my bundle' : 'Currently out of stock';
          }
        }
        Array.prototype.forEach.call(stage.querySelectorAll('.fq-bld-flavor'), function (row) {
          var fp = row.getAttribute('data-flavor');
          row.querySelector('[data-inc]').addEventListener('click', function () { if (totalQty(qty) < slotsFor(chosenPlan, chosenAge) && avail(fp)) { qty[fp] = (qty[fp] || 0) + 1; refreshAll(); } });
          row.querySelector('[data-dec]').addEventListener('click', function () { if ((qty[fp] || 0) > 0) { qty[fp]--; refreshAll(); } });
        });
        Array.prototype.forEach.call(stage.querySelectorAll('.fq-age-opt'), function (btn) {
          btn.addEventListener('click', function () {
            chosenAge = btn.getAttribute('data-age');
            Array.prototype.forEach.call(stage.querySelectorAll('.fq-age-opt'), function (b) { var on = b === btn; b.classList.toggle('is-selected', on); b.setAttribute('aria-pressed', on); });
            clampQty(); refreshAll();
          });
        });
        Array.prototype.forEach.call(stage.querySelectorAll('.fq-plan'), function (btn) {
          btn.addEventListener('click', function () { setPlan(btn.getAttribute('data-plan')); });
        });
        // Phase 1 → Phase 2: capture email, then reveal the recommendations.
        var gate = stage.querySelector('[data-fq-gate]');
        var rec = stage.querySelector('[data-fq-recs]');
        var revealBtn = stage.querySelector('[data-fq-reveal]');
        var email = stage.querySelector('#fqEmail');
        function validate() { if (revealBtn) revealBtn.disabled = !isEmail(email.value); }
        if (email) { email.addEventListener('input', validate); validate(); }
        if (revealBtn) revealBtn.addEventListener('click', function () {
          capturedEmail = email ? email.value.trim() : '';
          console.log('[FizzyIQ quiz] email captured', { email: capturedEmail || null, answers: state });
          captureLead(capturedEmail);   // create the Shopify customer + tag with quiz answers
          if (gate) gate.hidden = true;
          if (rec) rec.hidden = false;
          refreshAll();
          if (rec) { try { rec.scrollIntoView({ block: 'nearest' }); } catch (e) {} }
        });
        if (addBtn) addBtn.addEventListener('click', function () { finishQuiz(qty, chosenAge, chosenPlan, addBtn); });
        refreshAll();
      }
    }

    function isEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim()); }

    // Linear flow, skipping any step whose showIf(state) is false. Fully reversible.
    function advance() {
      for (var i = current + 1; i < STEPS.length; i++) {
        if (isVisible(STEPS[i])) { current = i; render(true); return; }
      }
    }
    function back() {
      for (var i = current - 1; i >= 0; i--) {
        if (isVisible(STEPS[i])) { current = i; render(true); return; }
      }
      close();   // already on the first visible step → back out of the quiz
    }

    // Create a Shopify customer (email + quiz answers as tags) via the storefront
    // customer form — Shopify honors contact[tags]; no server/Admin API needed. Sam 2026-06-08.
    function quizTags() {
      var t = ['FizzyQuiz'];
      var FN = { bc: 'berry-cherry', sl: 'strawberry-lemonade', wm: 'watermelon', ob: 'orange-creamsicle' };
      if (state.audience) t.push('quiz-for:' + state.audience);
      if (state.age) t.push('quiz-age:' + state.age);
      (Array.isArray(state.priority) ? state.priority : (state.priority ? [state.priority] : [])).forEach(function (p) { t.push('quiz-goal:' + p); });
      if (state.stimulant) t.push('quiz-stimulant:' + state.stimulant);
      if (state.feeling) t.push('quiz-feeling:' + state.feeling);
      (Array.isArray(state.flavor) ? state.flavor : (state.flavor ? [state.flavor] : [])).forEach(function (f) { t.push('quiz-flavor:' + (FN[f] || f)); });
      return t;
    }
    function captureLead(email) {
      if (!email) return;
      var form = document.getElementById('fizzyLeadForm');
      var em = document.getElementById('fqLeadEmail');
      var tg = document.getElementById('fqLeadTags');
      if (form && em && tg) {
        em.value = email;
        tg.value = quizTags().join(', ');
        // Submit the REAL Shopify form (into the hidden iframe) so spam protection passes.
        try { if (form.requestSubmit) { form.requestSubmit(); } else { form.submit(); } return; } catch (e) {}
      }
      // Fallback — raw POST (may be dropped by Shopify's form spam protection).
      try {
        var body = new URLSearchParams();
        body.set('form_type', 'customer'); body.set('utf8', '✓');
        body.set('contact[email]', email); body.set('contact[tags]', quizTags().join(', '));
        fetch('/contact', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() }).catch(function () {});
      } catch (e) {}
    }

    function finishQuiz(qty, ageTierVal, planParam, btn) {
      var aud = state.audience || '';
      var primary = primaryFlavor(qty);
      var v = variantFor(primary, ageTierVal, planParam);

      // Developer's backend contract (matches their live buy box): post the bundle
      // variant (primary flavor × age × quantity) + Age Range + one Flavor i per
      // pouch slot, plus the variant's own selling plan. The bundle app adds the
      // component pouch line itself; we never add it. Try It Once → no selling_plan.
      var units = expandUnits(qty);                 // one entry per pouch slot
      var props = {};
      if (v) {
        var ageStr = AGE_OPT[ageTierVal] || '';
        (v.options || []).forEach(function (o) { if (normOpt(o) === normOpt(ageStr)) ageStr = o; });
        props['Age Range'] = ageStr;
        units.forEach(function (u, i) { props['Flavor ' + (i + 1)] = storeOptionName(u.param); });
      }
      var addItem = { id: v && v.id, quantity: 1 };
      // Selling plan: prefer the variant's own allocation (store-independent).
      // Fall back to the hardcoded prod ids ONLY if this store has plans at all —
      // on the dev store (no Skio) every sp is null and we omit it (one-time add).
      var storeHasPlans = (window.__fizzyVariants || []).some(function (x) { return x.sp != null; });
      var sp = null;
      if (planParam !== 'once') sp = (v && v.sp != null) ? v.sp : (storeHasPlans ? SELLING_PLAN[planParam] : null);
      if (sp) addItem.selling_plan = sp;
      if (Object.keys(props).length) addItem.properties = props;
      console.log('[FizzyIQ quiz] add', { variant: v && v.id, plan: planParam, selling_plan: sp || null, props: props, discount: DISCOUNT_CODE });

      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      function fallbackToPDP() { window.location.href = PRODUCT_URL + (v && v.id ? '?variant=' + v.id : ''); }
      if (!v || !v.id) { fallbackToPDP(); return; }

      fetch('/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [addItem] })
      })
        .then(function (r) { if (!r.ok) return r.json().then(function (e) { throw e; }); return r.json(); })
        .then(function () {
          state = {}; current = 0;   // completed → a future open starts fresh
          // Tag the order audience (for the "add one for me" cart upsell on any cart
          // views), then go straight to checkout — the discount URL applies QUIZ10
          // (10% off) and redirects to checkout with the configured bundle. Sam 2026-06-08.
          function go() { window.location.href = '/discount/' + DISCOUNT_CODE + '?redirect=/checkout'; }
          if (aud) {
            return fetch('/cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { quiz_for: aud } }) }).then(go, go);
          }
          go();
        })
        .catch(fallbackToPDP);
    }

    /* ── Open / close ────────────────────────────────────────────────── */
    function open() {
      window.fizzyPopupMotion.reveal(root, function () {
        lastFocus = document.activeElement;
        document.body.classList.remove('mobile-menu-open');
        var mm = document.getElementById('mobileMenu'); if (mm) mm.classList.remove('open');
        var sc = document.getElementById('mobileMenuScrim'); if (sc) sc.classList.remove('open');
        // Progress persists in memory across close/reopen; a browser refresh resets it.
        if (current < 0 || current >= STEPS.length) current = 0;
        root.hidden = false;
        root.classList.add('is-open');
        root.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        render(false);
      });
    }
    function close() {
      window.fizzyPopupMotion.dismiss(root, function () {
        root.classList.remove('is-open');
        root.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        root.hidden = true;
        if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
      });
    }

    root.querySelector('[data-fq-close]').addEventListener('click', close);
    if (backBtn) backBtn.addEventListener('click', back);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !root.hidden) close();
    });

    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-fizzy-quiz-open], a[href*="#quiz"], a[href$="#take-the-quiz"]');
      if (!t) return;
      e.preventDefault();
      open();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
